import type { ComponentType, SVGProps } from 'react';

import type { IconComponent, IconProps } from './types';

/**
 * Wraps a `?react` SVG as a Design System 2.0 icon, applying the defaults every icon shares.
 *
 * The contract it implements already existed: `Alert.tsx` types its severity glyphs as
 * `ComponentType<SVGProps<SVGSVGElement>>`, which a `?react` SVG satisfies and so does a wrapped
 * Phosphor icon. This layer makes that explicit, so a Phosphor-derived glyph and a Natter-drawn one
 * are indistinguishable at a call site and an icon can move between the two without touching a
 * single consumer.
 *
 * Deliberately NOT a `forwardRef`. `svgr()` runs at `vite.config.ts:30` with no options and SVGR's
 * `ref` defaults to false, so a forwarded ref would never reach the SVG — a silent no-op is worse
 * than an unavailable feature. `IconProps` omits `ref` to say so at the type level.
 *
 * @param Svg the `?react` import
 * @param displayName the icon's export name, e.g. `MagnifyingGlassIcon`
 */
export const createIcon = (Svg: ComponentType<SVGProps<SVGSVGElement>>, displayName: string): IconComponent => {
  const Icon = ({ size = 16, ...rest }: IconProps) => {
    // Decorative by default. An icon that HAS been named must not also be hidden, or assistive tech
    // skips an element that exists precisely to be announced. Checked by value rather than with
    // `in`, so an explicitly-undefined `aria-label` still counts as unnamed.
    const named = rest['aria-label'] != null || rest['aria-labelledby'] != null;

    return (
      <Svg
        aria-hidden={named ? undefined : true}
        // Never a tab stop, in any browser. IE/Edge legacy made SVGs focusable by default and
        // Chrome still exposes `focusable`; wrap the icon in a button if it needs to be operable.
        focusable="false"
        height={size}
        width={size}
        // Last, so `className` and `style` reach the SVG. `width`, `height`, `fill`, `stroke` and
        // the a11y keys are all removed from `IconProps`, so nothing here can collide with the
        // attributes above.
        {...rest}
      />
    );
  };

  Icon.displayName = displayName;

  return Icon;
};
